import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import type { ConnectionPort } from "@/workflow/types/workflow";
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from "@xyflow/react";
import { create } from "zustand";
import {
  apiConnectionsToConnection,
  getNodeDisplayName,
  NodeTypes,
  type ApiNode,
  type Input,
  type NodeData,
  type RefName,
  type Edge as WorkflowApiEdge,
} from "../types/workflow";

interface WorkflowState {
  // Core data
  nodes: Node[];
  edges: Edge[];
  name: string;
  nodeConnecting: string | null;
  flowId: string;
  setFlowId: (id: string) => void;
  setNodeConnecting: (nodeId: string | null) => void;
  updateFlowName: (val: string) => void;
  parseApiNodesToReactFlowNodes: (val: { nodes: ApiNode[] }) => void;
  isHandleConnected: (nodeId: string, handle?: string) => boolean;
  deletingNode: string;
  setDeletingNode: (nodeId: string) => void;
  getNodeById: (nodeId: string) => Node | undefined;
  updateNodeOutputs: (nodeId: string, outputs: ConnectionPort[]) => void;
  setNodeConnections: (
    nodeId: string,
    connections: Record<
      string,
      { display_name: string; target_node: string | null }
    >,
  ) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  selectedNodes: string[];
  selectedEdges: string[];
  setSelectedNodes: (nodeIds: string[]) => void;
  setSelectedEdges: (edgeIds: string[]) => void;
  clearSelection: () => void;
  lastClickPosition: { x: number; y: number };
  setLastClickPosition: (position: { x: number; y: number }) => void;
  selectedNodeId: string;
  setSelectedNodeId: (id: string) => void;
  createNodeAndEdge: (val: {
    type: RefName;
    position: { x: number; y: number };
    source: {
      source: string;
      source_handler: string;
    };
  }) => ApiNode | undefined;
  createOrphanNode: (val: {
    type: RefName;
    position: { x: number; y: number };
  }) => ApiNode | undefined;
  deleteNode: (nodeId: string) => void;
  removeWaitNodeConnection: (
    waitNodeId: string,
    threadHandle: string,
    sourceNodeId: string,
    sourceHandle: string,
  ) => NodeData["inputs"] | undefined;
  getNodesVariabes: (id: string) => Node[];
  getInteroperabilityNodesUpstreamFromIf: (ifNodeId: string) => Node[];
  reset: () => void;
  getFormsAvailableForCorrection: (correctionId: string) => Node[];
  getDocumentsAvailableForSigning: (signNodeId: string) => Node[];
  executionId?: string;
  setExecutionData: (
    data?: ApiFlowsExecutionsResponseType["data"][number],
  ) => void;
  executionData?: ApiFlowsExecutionsResponseType["data"][number];
}

const createGeneralNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  ref_name: RefName,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: ref_name,
    display_name: `${getNodeDisplayName(ref_name)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "GENERAL",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
  };
};

const createFormNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.FORM as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.FORM as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "FORM",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
  };
};

const createIfNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.IF as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.IF as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "IF_CONDITION",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      true: {
        display_name: "True",
      },
      false: {
        display_name: "False",
      },
    },
  };
};

const createSwitchNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.SWITCH as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.SWITCH as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "SWITCH_CONDITION",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      condition_1: {
        display_name: "Salida 1",
      },
      default: {
        display_name: "Por defecto",
      },
    },
  };
};

const createRevisionNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.CORRECTION as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.CORRECTION as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "REVISION",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      success: {
        display_name: "Aprobado",
      },
      error: {
        display_name: "Rechazado",
      },
    },
  };
};

const createDocGeneratorNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.DOC_GENERATOR as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.DOC_GENERATOR as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "DOCUMENT_GENERATOR",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      success: {
        display_name: "Éxito",
      },
      error: {
        display_name: "Error",
      },
    },
  };
};

const createPaymentNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.PAYMENT as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.PAYMENT as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "PAYMENT",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      success: {
        display_name: "Éxito",
      },
      error: {
        display_name: "Error",
      },
    },
  };
};

const createSignDocumentNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.SIGN_DOCUMENT as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.SIGN_DOCUMENT as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "DOCUMENT_GENERATOR", // Temporalmente usando DOCUMENT_GENERATOR para compatibility con la API
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      success: {
        display_name: "Éxito",
      },
      error: {
        display_name: "Error",
      },
    },
  };
};

const createMultiThreadNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.MULTITHREAD as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.MULTITHREAD as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "MULTITHREAD",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      thread_1: {
        display_name: "Salida 1",
      },
      thread_2: {
        display_name: "Salida 2",
      },
    },
  };
};

const createWaitNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  // Wait nodes start with empty inputs array
  // THREAD inputs will be added when editing the node and selecting a multithread node

  return {
    ref_name: NodeTypes.WAIT as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.WAIT as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "WAIT",
    order,
    edge: undefined, // Wait nodes don't use edge field, they use THREAD inputs for connections
    inputs: [], // Wait nodes start with empty inputs, THREAD inputs are added when editing
    connections: {
      success: {
        display_name: "Continuar",
      },
      timeout: {
        display_name: "Timeout",
      },
    },
  };
};

const createDataCenterNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  ref_name: RefName,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: ref_name,
    display_name: `${getNodeDisplayName(ref_name)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "DATA_CENTER",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      success: {
        display_name: "Resultado",
      },
      error: {
        display_name: "Error",
      },
    },
  };
};

const createInteroperabilityNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.INTEROPERABILITY as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.INTEROPERABILITY as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "INTEROPERABILITY",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      success: {
        display_name: "Resultado",
      },
      error: {
        display_name: "Error",
      },
    },
  };
};

const createN8NNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.N8N as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.N8N as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "N8N",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
    connections: {
      success: {
        display_name: "Resultado",
      },
      error: {
        display_name: "Error",
      },
    },
  };
};

const createResolutionNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.RESOLUTION as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.RESOLUTION as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "GENERAL",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
  };
};

const createMergeNode = (
  position: {
    x: number;
    y: number;
  },
  order: number,
  nodeTypeCount: number,
  connection?: {
    source: string;
    source_handler: string;
  },
): ApiNode => {
  return {
    ref_name: NodeTypes.MERGE as RefName,
    display_name: `${getNodeDisplayName(NodeTypes.MERGE as RefName)}_${nodeTypeCount}${connection?.source_handler ? `_${connection?.source_handler}` : ""}`,
    position_x: position.x,
    position_y: position.y,
    type: "MERGE",
    order: order,
    ...(connection && {
      edge: {
        source: connection?.source || "",
        source_handler: connection?.source_handler || "",
        target_handler: "target",
      },
    }),
  };
};

const createNode = ({
  type,
  position,
  source,
  order,
  nodeTypeCount,
}: {
  type: RefName;
  position: { x: number; y: number };
  nodeTypeCount: number;
  source?: {
    source: string;
    source_handler: string;
  };
  order: number;
}): ApiNode | undefined => {
  if (type === NodeTypes.IF) {
    return createIfNode(position, order + 1, nodeTypeCount + 1, source);
  }
  if (type === NodeTypes.SWITCH) {
    return createSwitchNode(position, order + 1, nodeTypeCount + 1, source);
  }
  if (type === NodeTypes.FORM) {
    return createFormNode(position, order + 1, nodeTypeCount + 1, source);
  }

  if (type === NodeTypes.CORRECTION) {
    return createRevisionNode(position, order + 1, nodeTypeCount + 1, source);
  }

  if (type === NodeTypes.DOC_GENERATOR) {
    return createDocGeneratorNode(
      position,
      order + 1,
      nodeTypeCount + 1,
      source,
    );
  }

  if (type === NodeTypes.PAYMENT) {
    return createPaymentNode(position, order + 1, nodeTypeCount + 1, source);
  }

  if (type === NodeTypes.SIGN_DOCUMENT) {
    return createSignDocumentNode(
      position,
      order + 1,
      nodeTypeCount + 1,
      source,
    );
  }

  if (type === NodeTypes.DATA_CENTER) {
    return createDataCenterNode(
      position,
      order + 1,
      nodeTypeCount + 1,
      type,
      source,
    );
  }

  if (type === NodeTypes.MULTITHREAD) {
    return createMultiThreadNode(
      position,
      order + 1,
      nodeTypeCount + 1,
      source,
    );
  }

  if (type === NodeTypes.WAIT) {
    return createWaitNode(position, order + 1, nodeTypeCount + 1, source);
  }

  if (type === NodeTypes.INTEROPERABILITY) {
    return createInteroperabilityNode(
      position,
      order + 1,
      nodeTypeCount + 1,
      source,
    );
  }

  if (type === NodeTypes.N8N) {
    return createN8NNode(position, order + 1, nodeTypeCount + 1, source);
  }

  if (type === NodeTypes.RESOLUTION) {
    return createResolutionNode(position, order + 1, nodeTypeCount + 1, source);
  }

  if (type === NodeTypes.MERGE) {
    return createMergeNode(position, order + 1, nodeTypeCount + 1, source);
  }

  return createGeneralNode(
    position,
    order + 1,
    nodeTypeCount + 1,
    type,
    source,
  );
};

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  //initialState
  nodes: [],
  edges: [],
  name: "",
  selectedNodes: [],
  selectedEdges: [],
  flowId: "",
  setFlowId: (id: string) => set({ flowId: id }),
  updateFlowName: (val: string) => set({ name: val }),
  nodeConnecting: null,
  setNodeConnecting: (nodeId: string | null) => {
    set({ nodeConnecting: nodeId });
  },
  parseApiNodesToReactFlowNodes({ nodes: apiNodes }) {
    const { nodes: existingNodes, edges: existingEdges } = get();

    const waitThreadConnectionsFromState = new Map<
      string,
      Array<{
        source_node_id: string;
        source_node_output_id: string | null;
        field?: {
          id: string;
          name?: string;
          type?: string;
        };
      }>
    >();

    existingEdges.forEach((edge) => {
      if (!edge.targetHandle || !edge.targetHandle.startsWith("thread_"))
        return;
      if (!edge.target) return;
      const key = `${edge.target}|${edge.targetHandle}`;
      const current = waitThreadConnectionsFromState.get(key) || [];
      current.push({
        source_node_id: edge.source || "",
        source_node_output_id: edge.sourceHandle || null,
        field: edge.sourceHandle
          ? {
              id: edge.sourceHandle,
              name: edge.sourceHandle,
              type: "thread",
            }
          : undefined,
      });
      waitThreadConnectionsFromState.set(key, current);
    });

    // Build reverse lookup: wait input id -> source node id (from other nodes' target_connections)
    // This lets us render edges to WAIT nodes even if the backend omits inputs[*].source_connections.
    const inputIdToSourceConnections = new Map<
      string,
      Array<{
        source_node_id: string;
        source_node_output_id: string | null;
        field?: {
          id: string;
          name?: string;
          type?: string;
        };
      }>
    >();
    apiNodes.forEach((node) => {
      const targets = node.target_connections as
        | Array<{
            target_node_input_id?: string | null;
            source_node_id?: string | null;
            source_node_output_id?: string | null;
            ref_name?: string | null;
            display_name?: string | null;
          }>
        | undefined;
      if (!targets) return;
      targets.forEach((tc) => {
        if (tc?.target_node_input_id && tc?.source_node_id) {
          const existing = inputIdToSourceConnections.get(
            tc.target_node_input_id,
          );
          const next = [
            ...(existing || []),
            {
              source_node_id: tc.source_node_id,
              source_node_output_id: tc.source_node_output_id ?? null,
              field: tc.ref_name
                ? {
                    id: tc.ref_name,
                    name: tc.display_name || tc.ref_name,
                    type: "thread",
                  }
                : undefined,
            },
          ];
          inputIdToSourceConnections.set(tc.target_node_input_id, next);
        }
      });
    });

    // Enrich WAIT node inputs with synthesized source_connections when missing
    apiNodes.forEach((node) => {
      if (node.ref_name !== NodeTypes.WAIT || !node.inputs) return;
      let threadIndex = 0;
      node.inputs = node.inputs.map((input): Input[number] => {
        if (input?.type !== "THREAD") {
          return input;
        }

        threadIndex += 1;
        const threadHandle = `thread_${threadIndex}`;
        const guessedConnections = input.id
          ? inputIdToSourceConnections.get(input.id)
          : undefined;
        const fallbackConnections =
          waitThreadConnectionsFromState.get(`${node.id}|${threadHandle}`) ||
          [];

        const mergedConnections: Array<{
          source_node_id: string;
          source_node_output_id: string | null;
          field?: {
            id: string;
            name?: string;
            type?: string;
          };
        }> = [];

        const appendConnections = (
          connections:
            | Array<{
                source_node_id: string;
                source_node_output_id: string | null;
                field?: {
                  id: string;
                  name?: string;
                  type?: string;
                };
              }>
            | undefined,
        ) => {
          connections?.forEach((connection) => {
            if (!connection?.source_node_id) return;
            const alreadyExists = mergedConnections.some(
              (existing) =>
                existing.source_node_id === connection.source_node_id &&
                (existing.field?.id || null) === (connection.field?.id || null),
            );
            if (!alreadyExists) {
              mergedConnections.push(connection);
            }
          });
        };

        // If source_connections is explicitly defined (including empty array), trust the API data
        // Only use fallbacks when source_connections is undefined/missing
        const hasExplicitSourceConnections = Array.isArray(
          input.source_connections,
        );

        appendConnections(
          input.source_connections as
            | Array<{
                source_node_id: string;
                source_node_output_id: string | null;
                field?: {
                  id: string;
                  name?: string;
                  type?: string;
                };
              }>
            | undefined,
        );

        // Only use guessed and fallback connections if API didn't provide source_connections
        if (!hasExplicitSourceConnections) {
          appendConnections(guessedConnections);
          appendConnections(fallbackConnections);
        }

        const patchedInput = {
          ...input,
          source_connections:
            mergedConnections as Input[number]["source_connections"],
        };

        if (
          (!patchedInput.connection || patchedInput.connection.length === 0) &&
          mergedConnections.length > 0
        ) {
          patchedInput.connection = mergedConnections[0].source_node_id;
        }
        if (
          (!patchedInput.value || typeof patchedInput.value !== "string") &&
          mergedConnections[0]?.field?.id
        ) {
          patchedInput.value = mergedConnections[0].field?.id;
        }

        return patchedInput;
      });
    });

    // Create a map of existing nodes for quick lookup
    const existingNodeMap = new Map(
      existingNodes.map((node) => [node.id, node]),
    );

    // Create nodes, preserving existing positions where possible
    const nodes: Node[] = apiNodes.map((node) => {
      const existingNode = existingNodeMap.get(node.id!);
      return {
        id: node.id!,
        ...(node.ref_name === NodeTypes.INITIALIZER && {
          draggable: false,
          deletable: false,
        }),
        extent: "parent",
        position: existingNode
          ? existingNode.position
          : {
              x: node.position_x,
              y: node.position_y,
            },
        type: "worflow-node",
        data: {
          type: node.ref_name,
          node_type: node.type,
          displayName: node.display_name,
          order: node.order,
          edge: node.edge ?? null,
          edges: node.edges ?? [],
          inputs: node.inputs ?? [],
          manual_continue: node.manual_continue,
          connections: (() => {
            // For MultiThread nodes, use the connections field directly
            if (node.ref_name === NodeTypes.MULTITHREAD) {
              if (
                node.target_connections &&
                node.target_connections.length > 0
              ) {
                // Convert connections to the expected format using apiConnectionsToConnection
                return apiConnectionsToConnection(node.target_connections);
              }
              // Try to parse from inputs if connections not available
              if (node.inputs && node.inputs.length > 0) {
                const result: Record<
                  string,
                  { display_name: string; target_node: string | null }
                > = {};
                node.inputs.forEach((input) => {
                  if (input.name?.startsWith("thread_")) {
                    result[input.name] = {
                      display_name: input.value as string,
                      target_node: null,
                    };
                  }
                });
                if (Object.keys(result).length > 0) {
                  return result;
                }
              }
              // Fallback to default 2 threads for MultiThread if no inputs found
              return {
                thread_1: { display_name: "Salida 1", target_node: null },
                thread_2: { display_name: "Salida 2", target_node: null },
              };
            }

            if (node.ref_name === NodeTypes.WAIT) {
              return {
                success: {
                  display_name: "Continuar",
                  target_node: null,
                },
                timeout: {
                  display_name: "Timeout",
                  target_node: null,
                },
              };
            }

            // If target_connections exists and has data, use it
            // For other nodes, use target_connections
            if (node.target_connections && node.target_connections.length > 0) {
              let connections = apiConnectionsToConnection(
                node.target_connections,
              );
              if (
                node.ref_name === NodeTypes.INTEROPERABILITY &&
                connections["success"]
              ) {
                connections = {
                  ...connections,
                  success: connections["success"],
                  error: connections["error"] || {
                    display_name: "Error",
                    target_node: null,
                  },
                };
              }

              return connections;
            }
            return {};
          })(),
          ...(node.conditions && { conditions: node.conditions }),
          ...(node.calculations && { calculations: node.calculations }),
        },
      };
    });

    const toReactFlowEdge = (
      apiEdge: WorkflowApiEdge,
      fallbackTargetId: string,
    ): Edge | null => {
      const sourceId = apiEdge.source;
      const targetId = apiEdge.target ?? fallbackTargetId;
      if (!sourceId || !targetId) return null;

      const normalizedTargetHandle = apiEdge.target_handler;

      const edgeId =
        apiEdge.id ||
        `${sourceId}-${targetId}-${apiEdge.source_handler}-${apiEdge.target_handler}`;

      return {
        id: edgeId,
        source: sourceId,
        target: targetId,
        sourceHandle: apiEdge.source_handler,
        targetHandle: normalizedTargetHandle,
        type: "workflow-edge",
        data: {
          source: {
            id: sourceId,
            handle: apiEdge.source_handler,
          },
          target: {
            id: targetId,
            handle: normalizedTargetHandle,
          },
        },
      };
    };

    const registerEdge = (edge: Edge | null, edgeMap: Map<string, Edge>) => {
      if (!edge) return;
      const key =
        edge.id ||
        `${edge.source}-${edge.target}-${edge.sourceHandle}-${edge.targetHandle}`;
      if (edgeMap.has(key)) return;
      edgeMap.set(key, edge);
    };

    const edgeMap = new Map<string, Edge>();

    apiNodes.forEach((node) => {
      if (node.edge) {
        registerEdge(toReactFlowEdge(node.edge, node.id!), edgeMap);
      }

      if (Array.isArray(node.edges)) {
        node.edges.forEach((apiEdge) => {
          registerEdge(toReactFlowEdge(apiEdge, node.id!), edgeMap);
        });
      }
    });

    const threadEdges: Edge[] = apiNodes
      .filter(
        (node) =>
          (node.ref_name === NodeTypes.WAIT ||
            node.ref_name === NodeTypes.MERGE) &&
          node.inputs,
      )
      .flatMap((node) => {
        const isMerge = node.ref_name === NodeTypes.MERGE;
        const inputTypeFilter = isMerge ? "MERGE_ENTRY" : "THREAD";
        const handlePrefix = isMerge ? "merge-input-" : "input-";

        const multiInputs =
          node.inputs?.filter((input) => input.type === inputTypeFilter) || [];

        // Crear mapa de edges existentes: "sourceNodeId-targetHandler" -> source_handler
        const existingEdgesMap = new Map<string, string>();
        if (node.edges && node.edges.length > 0) {
          node.edges.forEach((edge) => {
            if (edge.target_handler && edge.source_handler && edge.source) {
              existingEdgesMap.set(
                `${edge.source}-${edge.target_handler}`,
                edge.source_handler,
              );
            }
          });
        }

        return multiInputs.flatMap((input, inputIndex) => {
          if (
            !Array.isArray(input.source_connections) ||
            input.source_connections.length === 0
          ) {
            return [];
          }

          const targetHandle = `${handlePrefix}${inputIndex + 1}`;

          return input.source_connections
            .map((connection, connectionIdx) => {
              const sourceNodeId = connection?.source_node_id;
              if (!sourceNodeId) return null;

              // Prioridad para source_handler:
              // 1. field.id (si existe)
              // 2. source_node_output_id
              // 3. Edge existente del backend
              // 4. Fallback
              let sourceHandle =
                connection?.field?.id || connection?.source_node_output_id;

              const sourceNode = apiNodes.find((n) => n.id === sourceNodeId);
              if (sourceNode) {
                const singleHandleNodes = [
                  NodeTypes.CALCULATED,
                  NodeTypes.FORM,
                  NodeTypes.INITIALIZER,
                  NodeTypes.ASSIGN_GROUP,
                  NodeTypes.EXPEDIENTE,
                  NodeTypes.MERGE,
                  NodeTypes.RESOLUTION,
                ] as string[];
                if (singleHandleNodes.includes(sourceNode.ref_name)) {
                  sourceHandle = "source";
                }
              }

              if (!sourceHandle) {
                sourceHandle =
                  existingEdgesMap.get(`${sourceNodeId}-${targetHandle}`) ||
                  "source";
              }

              const edge: Edge = {
                id: `${sourceNodeId}-${node.id}-${targetHandle}-${connectionIdx}`,
                source: sourceNodeId,
                target: node.id!,
                sourceHandle: sourceHandle,
                targetHandle: targetHandle,
                type: "workflow-edge" as const,
                data: {
                  source: {
                    id: sourceNodeId,
                    handle: sourceHandle,
                  },
                  target: {
                    id: node.id,
                    handle: targetHandle,
                  },
                },
              };
              return edge;
            })
            .filter((edge): edge is Edge => edge !== null);
        });
      })
      .filter((edge): edge is Edge => edge !== null);

    threadEdges.forEach((edge) => registerEdge(edge, edgeMap));

    set({ nodes, edges: Array.from(edgeMap.values()) });
  },
  createNodeAndEdge(data) {
    const { getNodeById, nodes } = get();
    const node = getNodeById(data.source.source);
    if (!node) return;
    const { length } = nodes.filter(({ data: _data }) => {
      return _data.type === data.type;
    });
    return createNode({
      ...data,
      order: node.data.order as number,
      nodeTypeCount: length,
    });
  },
  createOrphanNode(data) {
    const { nodes } = get();
    const { length } = nodes.filter(({ data: _data }) => {
      return _data.type === data.type;
    });
    return createNode({
      ...data,
      order: 9999,
      nodeTypeCount: length,
    });
  },
  isHandleConnected: (nodeId: string, handle: string = "") => {
    const { edges } = get();
    if (handle) {
      return edges.some(
        (edge) => edge.source === nodeId && edge.sourceHandle === handle,
      );
    }
    return edges.some((edge) => edge.source === nodeId);
  },
  deletingNode: "",
  setDeletingNode(nodeId) {
    set({ deletingNode: nodeId });
  },
  getNodeById(nodeId) {
    const { nodes } = get();
    return nodes.find((node) => node.id === nodeId);
  },
  updateNodeOutputs: (nodeId: string, outputs: ConnectionPort[]) => {
    set((state) => ({
      nodes: state.nodes.map((node) => {
        if (node.id === nodeId) {
          const newConnections: Record<
            string,
            { display_name: string; target_node: string | null }
          > = {};
          outputs.forEach((output) => {
            newConnections[output.id] = {
              display_name: output.label || output.id,
              target_node: null,
            };
          });

          return {
            ...node,
            data: {
              ...node.data,
              connections: newConnections,
            },
          };
        }
        return node;
      }),
    }));
  },
  setNodeConnections: (
    nodeId: string,
    connections: Record<
      string,
      { display_name: string; target_node: string | null }
    >,
  ) => {
    set((state) => ({
      nodes: state.nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              connections,
            },
          };
        }
        return node;
      }),
    }));
  },

  onNodesChange: (changes: NodeChange[]) => {
    const filteredChanges = changes.filter((c) => c.type !== "remove");
    if (filteredChanges.length === 0) return;
    set((state) => ({
      nodes: applyNodeChanges(filteredChanges, state.nodes),
    }));
  },
  onEdgesChange: (changes: EdgeChange[]) => {
    const filteredChanges = changes.filter((c) => c.type !== "remove");
    if (filteredChanges.length === 0) return;
    set((state) => ({
      edges: applyEdgeChanges(filteredChanges, state.edges),
    }));
  },
  setSelectedNodes: (nodeIds: string[]) => {
    set({ selectedNodes: nodeIds });
  },

  setSelectedEdges: (edgeIds: string[]) => {
    set({ selectedEdges: edgeIds });
  },
  clearSelection: () => {
    set({ selectedNodes: [] });
  },
  lastClickPosition: { x: 0, y: 0 },
  setLastClickPosition: (position: { x: number; y: number }) => {
    set({ lastClickPosition: position });
  },
  selectedNodeId: "",
  setSelectedNodeId: (id: string) => {
    set({ selectedNodeId: id });
  },

  deleteNode(nodeId) {
    const { nodes, edges } = get();
    set({
      nodes: nodes.filter((node) => node.id !== nodeId),
      edges: edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId,
      ),
    });
  },
  removeWaitNodeConnection(
    waitNodeId,
    threadHandle,
    sourceNodeId,
    sourceHandle,
  ) {
    const { nodes, edges, getNodeById } = get();
    const waitNode = getNodeById(waitNodeId);
    if (!waitNode) return undefined;

    const waitData = waitNode.data as unknown as NodeData;
    if (!Array.isArray(waitData.inputs)) return undefined;

    // threadHandle tiene formato: input-1, input-2, etc.
    const handleMatch = threadHandle.match(/^input-(\d+)$/);
    if (!handleMatch) return undefined;

    const inputIndex = parseInt(handleMatch[1], 10) - 1;
    const threadInputs = waitData.inputs.filter(
      (input) => input.type === "THREAD",
    );

    if (inputIndex < 0 || inputIndex >= threadInputs.length) return undefined;

    // Encontrar el índice real en el array de inputs
    let threadCount = 0;
    let targetInputArrayIndex = -1;
    for (let i = 0; i < waitData.inputs.length; i++) {
      if (waitData.inputs[i].type === "THREAD") {
        if (threadCount === inputIndex) {
          targetInputArrayIndex = i;
          break;
        }
        threadCount++;
      }
    }

    if (targetInputArrayIndex === -1) return undefined;

    // Crear inputs actualizados removiendo la conexión
    const updatedInputs = waitData.inputs.map((input, idx) => {
      if (idx !== targetInputArrayIndex) return { ...input };

      const filteredConnections = Array.isArray(input.source_connections)
        ? input.source_connections.filter((conn) => {
            const connHandle = conn?.field?.id || conn?.source_node_output_id;
            if (!connHandle) {
              return conn?.source_node_id !== sourceNodeId;
            }
            return !(
              conn?.source_node_id === sourceNodeId &&
              connHandle === sourceHandle
            );
          })
        : [];

      return {
        ...input,
        source_connections: filteredConnections,
        connection:
          filteredConnections.length > 0 ? input.connection : undefined,
      };
    });

    // Reconstruir edges con el nuevo formato input-{index}
    // Usar los edges existentes para lookup del source_handler
    const existingEdgesMap = new Map<string, string>();
    edges.forEach((edge) => {
      if (
        edge.target === waitNodeId &&
        edge.targetHandle &&
        edge.sourceHandle &&
        edge.source
      ) {
        existingEdgesMap.set(
          `${edge.source}-${edge.targetHandle}`,
          edge.sourceHandle,
        );
      }
    });

    const updatedThreadInputs = updatedInputs.filter(
      (input) => input?.type === "THREAD",
    );
    const rebuiltThreadEdges: Edge[] = updatedThreadInputs.flatMap(
      (input, idx) => {
        if (!Array.isArray(input.source_connections)) return [];

        const targetHandle = `input-${idx + 1}`;

        return input.source_connections
          .map((connection, connectionIdx) => {
            const connSourceNodeId = connection?.source_node_id;
            if (!connSourceNodeId) return null;

            // Prioridad: field.id -> source_node_output_id -> edge existente -> fallback
            let connSourceHandle =
              connection?.field?.id || connection?.source_node_output_id;
            if (!connSourceHandle) {
              connSourceHandle =
                existingEdgesMap.get(`${connSourceNodeId}-${targetHandle}`) ||
                "source";
            }

            return {
              id: `${connSourceNodeId}-${waitNodeId}-${targetHandle}-${connectionIdx}`,
              source: connSourceNodeId,
              target: waitNodeId,
              sourceHandle: connSourceHandle,
              targetHandle: targetHandle,
              type: "workflow-edge" as const,
              data: {
                source: {
                  id: connSourceNodeId,
                  handle: connSourceHandle,
                },
                target: {
                  id: waitNodeId,
                  handle: targetHandle,
                },
              },
            } as Edge;
          })
          .filter((edge): edge is Edge => Boolean(edge));
      },
    );

    // Remover todos los edges del Wait y agregar los reconstruidos
    const edgesWithoutWait = edges.filter(
      (edge) =>
        !(
          edge.target === waitNodeId && edge.targetHandle?.startsWith("input-")
        ),
    );

    // Actualizar nodos
    const updatedNodes = nodes.map((node) => {
      if (node.id !== waitNodeId) return node;
      return {
        ...node,
        data: {
          ...waitData,
          inputs: updatedInputs,
        },
      };
    });

    set({
      nodes: updatedNodes,
      edges: [...edgesWithoutWait, ...rebuiltThreadEdges],
    });

    return updatedInputs;
  },
  reset: () => {
    set({
      nodes: [],
      edges: [],
      name: "",
      selectedNodes: [],
      selectedEdges: [],
      nodeConnecting: null,
      deletingNode: "",
      lastClickPosition: { x: 0, y: 0 },
      selectedNodeId: "",
      executionId: "",
      executionData: undefined,
    });
  },
  getNodesVariabes(nodeId) {
    const { getNodeById } = get();
    const selectedNode = getNodeById(nodeId);
    if (!selectedNode) return [];

    const matchedNodesMap = new Map<string, Node>();
    const visited = new Set<string>();

    const findNodes = (currentNodeId: string) => {
      if (visited.has(currentNodeId)) return;
      visited.add(currentNodeId);

      const node = getNodeById(currentNodeId);
      if (!node) return;

      const data = node.data as unknown as NodeData;

      matchedNodesMap.set(node.id, node);

      if (data.edge?.source) {
        findNodes(data.edge.source);
      }

      if (Array.isArray(data.edges)) {
        data.edges.forEach((e) => {
          if (e.source) findNodes(e.source);
        });
      }

      if (Array.isArray(data.inputs)) {
        data.inputs.forEach((input) => {
          if (Array.isArray(input.source_connections)) {
            input.source_connections.forEach(
              (conn: { source_node_id?: string }) => {
                if (conn.source_node_id) findNodes(conn.source_node_id);
              },
            );
          }
        });
      }
    };

    const selectedData = selectedNode.data as unknown as NodeData;
    if (selectedData.edge?.source) {
      findNodes(selectedData.edge.source);
    }
    if (Array.isArray(selectedData.edges)) {
      selectedData.edges.forEach((e) => {
        if (e.source) findNodes(e.source);
      });
    }

    const resultNodes = Array.from(matchedNodesMap.values());

    const initializer = resultNodes.find((n) => {
      const data = n.data as unknown as NodeData;
      return data.type === NodeTypes.INITIALIZER;
    });
    const otherNodes = resultNodes.filter((n) => {
      const data = n.data as unknown as NodeData;
      return data.type !== NodeTypes.INITIALIZER;
    });

    return initializer ? [initializer, ...otherNodes] : otherNodes;
  },
  getInteroperabilityNodesUpstreamFromIf(ifNodeId: string) {
    const { getNodeById } = get();
    const ifNode = getNodeById(ifNodeId);
    if (!ifNode) return [];
    const ifNodeData = ifNode.data as unknown as NodeData;
    if (ifNodeData.type !== NodeTypes.IF) return [];

    const interoperabilityNodes: Node[] = [];
    const visited = new Set<string>();

    const findInteroperabilityNodes = (nodeId: string) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      const node = getNodeById(nodeId);
      if (!node) return;

      const nodeData = node.data as unknown as NodeData;
      if (nodeData.type === NodeTypes.INTEROPERABILITY) {
        interoperabilityNodes.push(node);
      }
      if (nodeData.edge) {
        findInteroperabilityNodes(nodeData.edge.source);
      }
    };
    if (ifNodeData.edge) {
      findInteroperabilityNodes(ifNodeData.edge.source);
    }

    return interoperabilityNodes;
  },
  getFormsAvailableForCorrection(correctionId) {
    const { getNodeById } = get();
    const correctionNode = getNodeById(correctionId);
    if (!correctionNode) return [];
    const correctionData = correctionNode.data as unknown as NodeData;
    const matchedNodes: Node[] = [];
    // available form for correction are those that are after the last if or switch before the correction node
    const findNodes = (edgeSource: string) => {
      const edgeNode = getNodeById(edgeSource);
      if (edgeNode) {
        const edgeData = edgeNode.data as unknown as NodeData;
        if (edgeData.type === NodeTypes.FORM) {
          matchedNodes.push(edgeNode);
        }
        if (
          edgeData.edge
          // &&
          // edgeData.type !== NodeTypes.IF &&
          // edgeData.type !== NodeTypes.SWITCH &&
          //edgeData.type !== NodeTypes.CORRECTION
        ) {
          findNodes(edgeData.edge.source);
        }
      }
    };
    if (correctionData.edge) {
      findNodes(correctionData.edge.source);
    }
    return matchedNodes;
  },
  getDocumentsAvailableForSigning(signNodeId) {
    const { getNodeById } = get();
    const signNode = getNodeById(signNodeId);
    if (!signNode) return [];
    const signData = signNode.data as unknown as NodeData;
    const matchedNodes: Node[] = [];

    // Buscar todos los nodos DOC_GENERATOR disponibles antes del nodo de firma
    const findNodes = (edgeSource: string) => {
      const edgeNode = getNodeById(edgeSource);
      if (edgeNode) {
        const edgeData = edgeNode.data as unknown as NodeData;
        if (edgeData.type === NodeTypes.DOC_GENERATOR) {
          matchedNodes.push(edgeNode);
        }
        // Continuar la búsqueda hacia atrás sin restricciones de tipo de nodo
        if (edgeData.edge) {
          findNodes(edgeData.edge.source);
        }
      }
    };

    if (signData.edge) {
      const immediatePreviousNode = getNodeById(signData.edge.source);
      const immediatePreviousData = immediatePreviousNode?.data as
        | NodeData
        | undefined;

      if (immediatePreviousData?.type === NodeTypes.MERGE) {
        return immediatePreviousNode ? [immediatePreviousNode] : [];
      }

      findNodes(signData.edge.source);
    }
    return matchedNodes;
  },
  executionId: "",
  setExecutionData(data) {
    set({ executionData: data });
  },
  executionData: undefined,
}));
